import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, GripVertical, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';

const TABS = [
  { key: 'brand', label: 'Brand' },
  { key: 'security', label: 'Security' },
  { key: 'backup', label: 'Backup' },
  { key: 'ical', label: 'Calendar Sync' },
  { key: 'services', label: 'Services' },
  { key: 'inclusions', label: 'Inclusions Master' },
  { key: 'terms', label: 'Terms Library' },
  { key: 'reasons', label: 'Reason Codes' },
  { key: 'events', label: 'Event Types' },
  { key: 'sources', label: 'Enquiry Sources' },
  { key: 'roles', label: 'Crew Roles' },
  { key: 'docText', label: 'Document Text' },
  { key: 'travel', label: 'Travel & Surcharges' },
  { key: 'thresholds', label: 'Rules & Thresholds' },
  { key: 'defaults', label: 'Document Defaults' },
];

export default function Settings() {
  const [tab, setTab] = useState('brand');
  const [s, setS] = useState(null);
  const { logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get('/settings').then(r => setS(r.data)); }, []);
  if (!s) return null;

  const save = async (partial) => {
    const next = { ...s, ...partial };
    setS(next);
    await api.put('/settings', next);
    toast.success('Settings saved');
  };

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <div className="eyebrow">Config</div>
      <h1 className="serif text-[28px] mb-5">Settings</h1>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 -mx-5 px-5 lg:mx-0 lg:px-0">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className="px-3 py-1.5 rounded-full text-xs tracking-[0.14em] uppercase whitespace-nowrap"
            style={{background: tab===t.key?'var(--charcoal)':'transparent', color: tab===t.key?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}
            data-testid={`settings-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'brand' && <Brand s={s} save={save} logout={logout} nav={nav} />}
      {tab === 'security' && <Security logout={logout} nav={nav}/>}
      {tab === 'backup' && <Backup />}
      {tab === 'ical' && <ICalTab s={s} save={save}/>}
      {tab === 'services' && <ServicesTab />}
      {tab === 'inclusions' && <SimpleList endpoint="/inclusions-master" fields={[{k:'text', label:'Inclusion text', wide:true}]} title="Inclusion lines" testId="incl"/>}
      {tab === 'terms' && <TermsTab />}
      {tab === 'reasons' && <SimpleList endpoint="/reason-codes" fields={[{k:'code', label:'Code'},{k:'label', label:'Label', wide:true}]} title="Complimentary reason codes" testId="reason"/>}
      {tab === 'events' && <StringList s={s} save={save} field="event_types" title="Event types" testId="evtype"/>}
      {tab === 'sources' && <StringList s={s} save={save} field="enquiry_sources" title="Enquiry sources" testId="src"/>}
      {tab === 'roles' && <StringList s={s} save={save} field="crew_roles" title="Crew roles" testId="role"/>}
      {tab === 'docText' && <DocText s={s} save={save}/>}
      {tab === 'travel' && <TravelRules s={s} save={save}/>}
      {tab === 'thresholds' && <Thresholds s={s} save={save}/>}
      {tab === 'defaults' && <Defaults s={s} save={save}/>}
    </div>
  );
}

function Brand({ s, save, logout, nav }) {
  const [f, setF] = useState(s);
  useEffect(() => setF(s), [s]);
  return (
    <div className="card-cream p-5 space-y-3">
      <div><label className="eyebrow">Brand name</label><input value={f.brand_name} onChange={e => setF({...f, brand_name: e.target.value})} data-testid="settings-brand"/></div>
      <div><label className="eyebrow">Tagline</label><input value={f.tagline} onChange={e => setF({...f, tagline: e.target.value})}/></div>
      <div><label className="eyebrow">Location line</label><input value={f.location_line} onChange={e => setF({...f, location_line: e.target.value})}/></div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="eyebrow">Phone</label><input value={f.phone} onChange={e => setF({...f, phone: e.target.value})}/></div>
        <div><label className="eyebrow">Instagram</label><input value={f.instagram} onChange={e => setF({...f, instagram: e.target.value})}/></div>
        <div><label className="eyebrow">Email</label><input value={f.email} onChange={e => setF({...f, email: e.target.value})}/></div>
        <div><label className="eyebrow">Website</label><input value={f.website} onChange={e => setF({...f, website: e.target.value})}/></div>
      </div>
      <button className="btn-gold w-full" onClick={() => save(f)} data-testid="settings-save">Save Brand</button>
      <button className="btn-ghost w-full" onClick={() => { logout(); nav('/login'); }}>Sign out</button>
    </div>
  );
}

function StringList({ s, save, field, title, testId }) {
  const [list, setList] = useState(s[field] || []);
  useEffect(() => setList(s[field] || []), [s, field]);
  const add = () => setList([...list, '']);
  const upd = (i, v) => { const n = [...list]; n[i] = v; setList(n); };
  const rm = (i) => setList(list.filter((_,x) => x !== i));
  const move = (i, dir) => { const n = [...list]; const j = i + dir; if (j<0||j>=n.length) return; [n[i],n[j]] = [n[j],n[i]]; setList(n); };
  return (
    <div className="card-cream p-5">
      <div className="flex items-center justify-between mb-3"><h3 className="serif text-[20px]">{title}</h3>
        <button onClick={add} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid={`${testId}-add`}><Plus size={12} className="inline"/> Add</button>
      </div>
      {list.map((v, i) => (
        <div key={i} className="flex gap-2 mb-2 items-center">
          <button onClick={() => move(i,-1)} className="text-xs text-[var(--muted)]">↑</button>
          <button onClick={() => move(i,1)} className="text-xs text-[var(--muted)]">↓</button>
          <input value={v} onChange={e => upd(i, e.target.value)} data-testid={`${testId}-${i}`}/>
          <button onClick={() => rm(i)} className="text-[var(--overdue)]"><Trash2 size={14}/></button>
        </div>
      ))}
      <button className="btn-gold w-full mt-3" onClick={() => save({ [field]: list })} data-testid={`${testId}-save`}>Save</button>
    </div>
  );
}

function SimpleList({ endpoint, fields, title, testId }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(endpoint).then(r => setItems(r.data.sort((a,b) => (a.sort_order||0)-(b.sort_order||0))));
  useEffect(() => { load(); }, []);
  const upd = (id, patch) => setItems(items.map(x => x.id === id ? { ...x, ...patch } : x));
  const save = async (item) => { await api.put(`${endpoint}/${item.id}`, item); toast.success('Saved'); };
  const add = async () => { const body = Object.fromEntries(fields.map(f => [f.k, ''])); body.sort_order = items.length; await api.post(endpoint, body); load(); };
  const rm = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`${endpoint}/${id}`); load(); };
  return (
    <div className="card-cream p-5">
      <div className="flex items-center justify-between mb-3"><h3 className="serif text-[20px]">{title}</h3>
        <button onClick={add} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid={`${testId}-add`}><Plus size={12} className="inline"/> Add</button>
      </div>
      {items.map(item => (
        <div key={item.id} className="border-b hairline py-2 flex gap-2 items-center">
          {fields.map(f => (
            <input key={f.k} value={item[f.k]||''} onChange={e => upd(item.id, { [f.k]: e.target.value })} placeholder={f.label} className={f.wide?'flex-1':'w-32'} data-testid={`${testId}-${item.id}-${f.k}`}/>
          ))}
          <button onClick={() => save(item)} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">Save</button>
          <button onClick={() => rm(item.id)} className="text-[var(--overdue)]"><Trash2 size={14}/></button>
        </div>
      ))}
    </div>
  );
}

function TermsTab() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const load = () => api.get('/terms').then(r => setItems(r.data.sort((a,b) => (a.sort_order||0)-(b.sort_order||0))));
  useEffect(() => { load(); }, []);
  const upd = (id, patch) => setItems(items.map(x => x.id === id ? { ...x, ...patch } : x));
  const save = async (item) => { await api.put(`/terms/${item.id}`, item); toast.success('Saved'); };
  const add = async () => { await api.post('/terms', { title: 'New term', body: '', on_invoice: true, on_quotation: true, sort_order: items.length }); load(); };
  const rm = async (id) => { if (!window.confirm('Delete?')) return; await api.delete(`/terms/${id}`); load(); };
  return (
    <div className="card-cream p-5">
      <div className="flex items-center justify-between mb-3"><h3 className="serif text-[20px]">Terms & Conditions Library</h3>
        <button onClick={add} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid="term-add"><Plus size={12} className="inline"/> Add</button>
      </div>
      <div className="text-xs text-[var(--muted)] mb-3">Each term can be marked as default on invoices, quotations, or both. Placeholders like {'{advance_pct}'} and {'{validity_days}'} are filled at issue time.</div>
      {items.map(item => (
        <div key={item.id} className="border-b hairline py-3">
          <div className="flex gap-2 items-center">
            <button onClick={() => setExpanded(expanded===item.id?null:item.id)}>{expanded===item.id?<ChevronDown size={14}/>:<ChevronRight size={14}/>}</button>
            <input value={item.title} onChange={e => upd(item.id, { title: e.target.value })} className="flex-1"/>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" style={{width:'auto'}} checked={item.on_invoice} onChange={e => upd(item.id, { on_invoice: e.target.checked })}/> Inv</label>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" style={{width:'auto'}} checked={item.on_quotation} onChange={e => upd(item.id, { on_quotation: e.target.checked })}/> Qtn</label>
            <button onClick={() => save(item)} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">Save</button>
            <button onClick={() => rm(item.id)} className="text-[var(--overdue)]"><Trash2 size={14}/></button>
          </div>
          {expanded === item.id && (
            <textarea rows={3} value={item.body} onChange={e => upd(item.id, { body: e.target.value })} className="mt-2" data-testid={`term-body-${item.id}`}/>
          )}
        </div>
      ))}
    </div>
  );
}

function ServicesTab() {
  const [items, setItems] = useState([]);
  const [master, setMaster] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  const load = () => Promise.all([
    api.get('/services').then(r => setItems(r.data.sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))),
    api.get('/inclusions-master').then(r => setMaster(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const upd = (id, patch) => setItems(items.map(x => x.id === id ? { ...x, ...patch } : x));
  const save = async (item) => { await api.put(`/services/${item.id}`, item); toast.success('Saved'); };
  const add = async () => { await api.post('/services', { name: 'New Service', category: 'party', default_rate: 0, unit: 'per_person', inclusions: [], active: true, sort_order: items.length }); load(); };
  const rm = async (id) => { if (!window.confirm('Delete permanently?')) return; await api.delete(`/services/${id}`); load(); };
  const toggleInc = (id, text) => {
    const item = items.find(x => x.id === id);
    const has = item.inclusions.includes(text);
    upd(id, { inclusions: has ? item.inclusions.filter(x => x !== text) : [...item.inclusions, text] });
  };

  const CATS = ['bridal','shoots','party','hair_styling','addons','editorial'];
  const UNITS = ['per_person','per_event','per_look','per_hour','per_day','package'];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div><h3 className="serif text-[20px]">Service catalogue</h3><div className="text-xs text-[var(--muted)]">{items.filter(x => x.active).length} active · {items.filter(x => !x.active).length} archived</div></div>
        <div className="flex gap-2">
          <button onClick={() => setShowArchived(!showArchived)} className="text-xs text-[var(--muted)] tracking-[0.14em] uppercase">{showArchived?'Hide':'Show'} archived</button>
          <button onClick={add} className="btn-gold" data-testid="svc-add"><Plus size={14}/> New Service</button>
        </div>
      </div>
      {CATS.map(cat => {
        const catItems = items.filter(x => x.category === cat && (showArchived || x.active));
        if (!catItems.length) return null;
        return (
          <div key={cat} className="card-cream p-4">
            <div className="eyebrow mb-2">{cat.replace('_',' ')}</div>
            {catItems.map(item => (
              <div key={item.id} className="border-b hairline py-3 last:border-0" style={{opacity: item.active?1:0.5}}>
                <div className="flex gap-2 items-center flex-wrap">
                  <button onClick={() => setExpanded(expanded===item.id?null:item.id)}>{expanded===item.id?<ChevronDown size={14}/>:<ChevronRight size={14}/>}</button>
                  <input value={item.name} onChange={e => upd(item.id, { name: e.target.value })} className="flex-1 min-w-[180px]" data-testid={`svc-name-${item.id}`}/>
                  <select value={item.unit} onChange={e => upd(item.id, { unit: e.target.value })} className="w-32">{UNITS.map(u => <option key={u} value={u}>{u.replace('_',' ')}</option>)}</select>
                  <input type="number" value={item.default_rate} onChange={e => upd(item.id, { default_rate: parseFloat(e.target.value||0) })} className="w-24" placeholder="Rate"/>
                  <label className="flex items-center gap-1 text-xs"><input type="checkbox" style={{width:'auto'}} checked={item.active} onChange={e => upd(item.id, { active: e.target.checked })}/> Active</label>
                  <button onClick={() => save(item)} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase">Save</button>
                  <button onClick={() => rm(item.id)} className="text-[var(--overdue)]"><Trash2 size={14}/></button>
                </div>
                {expanded === item.id && (
                  <div className="mt-3 space-y-2 pl-6">
                    <div><label className="eyebrow">Description (printed on documents)</label><input value={item.description||''} onChange={e => upd(item.id, { description: e.target.value })}/></div>
                    <div><label className="eyebrow">Category</label><select value={item.category} onChange={e => upd(item.id, { category: e.target.value })}>{CATS.map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}</select></div>
                    <div>
                      <label className="eyebrow">Inclusions (tick to include, from master list)</label>
                      <div className="grid grid-cols-2 gap-1 mt-1">
                        {master.map(m => (
                          <label key={m.id} className="flex items-center gap-2 text-xs py-1">
                            <input type="checkbox" style={{width:'auto'}} checked={item.inclusions.includes(m.text)} onChange={() => toggleInc(item.id, m.text)}/>
                            {m.text}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function DocText({ s, save }) {
  const [f, setF] = useState(s.document_text || {});
  useEffect(() => setF(s.document_text || {}), [s]);
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="card-cream p-5 space-y-3">
      <h3 className="serif text-[20px]">Document text</h3>
      <div><label className="eyebrow">"Included" heading</label><input value={f.includes_heading||''} onChange={e => upd('includes_heading', e.target.value)} data-testid="doc-text-includes"/></div>
      <div><label className="eyebrow">Default thank-you note</label><input value={f.thank_you_default||''} onChange={e => upd('thank_you_default', e.target.value)}/></div>
      <div><label className="eyebrow">Default balance due note</label><input value={f.balance_due_note_default||''} onChange={e => upd('balance_due_note_default', e.target.value)}/></div>
      <div><label className="eyebrow">Value-given-away line label</label><input value={f.value_given_line||''} onChange={e => upd('value_given_line', e.target.value)}/></div>
      <div><label className="eyebrow">Quotation savings label</label><input value={f.quotation_savings_label||''} onChange={e => upd('quotation_savings_label', e.target.value)}/></div>
      <div><label className="eyebrow">Quotation note default</label><input value={f.quotation_note_default||''} onChange={e => upd('quotation_note_default', e.target.value)}/></div>
      <button className="btn-gold w-full" onClick={() => save({ document_text: f })}>Save</button>
    </div>
  );
}

function TravelRules({ s, save }) {
  const [f, setF] = useState(s.travel_rules || {});
  useEffect(() => setF(s.travel_rules || {}), [s]);
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="card-cream p-5 space-y-3">
      <h3 className="serif text-[20px]">Travel & surcharge rules</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="eyebrow">Per-km rate (₹)</label><input type="number" value={f.per_km_rate||0} onChange={e => upd('per_km_rate', parseFloat(e.target.value||0))} data-testid="travel-perkm"/></div>
        <div><label className="eyebrow">Included radius (km)</label><input type="number" value={f.included_radius_km||0} onChange={e => upd('included_radius_km', parseFloat(e.target.value||0))}/></div>
        <div><label className="eyebrow">Early-call threshold</label><input placeholder="e.g. 06:00 AM" value={f.early_call_threshold||''} onChange={e => upd('early_call_threshold', e.target.value)}/></div>
        <div><label className="eyebrow">Early-call surcharge (₹)</label><input type="number" value={f.early_call_amount||0} onChange={e => upd('early_call_amount', parseFloat(e.target.value||0))}/></div>
        <div><label className="eyebrow">Peak months (comma-sep, 1-12)</label><input value={(f.peak_months||[]).join(',')} onChange={e => upd('peak_months', e.target.value.split(',').map(x => parseInt(x)).filter(Boolean))}/></div>
        <div><label className="eyebrow">Peak season %</label><input type="number" value={f.peak_pct||0} onChange={e => upd('peak_pct', parseFloat(e.target.value||0))}/></div>
        <div><label className="eyebrow">Per-diem rate (₹)</label><input type="number" value={f.per_diem_rate||0} onChange={e => upd('per_diem_rate', parseFloat(e.target.value||0))}/></div>
      </div>
      <button className="btn-gold w-full" onClick={() => save({ travel_rules: f })}>Save</button>
    </div>
  );
}

function Thresholds({ s, save }) {
  const [f, setF] = useState(s.thresholds || {});
  useEffect(() => setF(s.thresholds || {}), [s]);
  const upd = (k, v) => setF({ ...f, [k]: v });
  return (
    <div className="card-cream p-5 space-y-3">
      <h3 className="serif text-[20px]">Rules & thresholds</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="eyebrow">Clash buffer (minutes)</label><input type="number" value={f.clash_buffer_minutes||0} onChange={e => upd('clash_buffer_minutes', parseInt(e.target.value||0))}/></div>
        <div><label className="eyebrow">Guest count solo max</label><input type="number" value={f.guest_count_solo_max||0} onChange={e => upd('guest_count_solo_max', parseInt(e.target.value||0))}/></div>
        <div><label className="eyebrow">Tentative hold (days)</label><input type="number" value={f.tentative_hold_days||0} onChange={e => upd('tentative_hold_days', parseInt(e.target.value||0))}/></div>
        <div><label className="eyebrow">Backup reminder (days)</label><input type="number" value={f.backup_reminder_days||0} onChange={e => upd('backup_reminder_days', parseInt(e.target.value||0))}/></div>
        <div><label className="eyebrow">Adjacent travel hours</label><input type="number" value={f.adjacent_travel_hours||0} onChange={e => upd('adjacent_travel_hours', parseInt(e.target.value||0))}/></div>
      </div>
      <button className="btn-gold w-full" onClick={() => save({ thresholds: f })}>Save</button>
    </div>
  );
}

function Defaults({ s, save }) {
  const [f, setF] = useState(s);
  useEffect(() => setF(s), [s]);
  return (
    <div className="card-cream p-5 space-y-3">
      <h3 className="serif text-[20px]">Document defaults</h3>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="eyebrow">Next invoice #</label><input type="number" value={f.invoice_next} onChange={e => setF({...f, invoice_next: parseInt(e.target.value||0)})}/></div>
        <div><label className="eyebrow">Quotation year</label><input value={f.quotation_next_year} onChange={e => setF({...f, quotation_next_year: e.target.value})}/></div>
        <div><label className="eyebrow">Next quotation seq</label><input type="number" value={f.quotation_next_seq} onChange={e => setF({...f, quotation_next_seq: parseInt(e.target.value||0)})}/></div>
        <div><label className="eyebrow">Default advance %</label><input type="number" value={f.default_advance_pct} onChange={e => setF({...f, default_advance_pct: parseInt(e.target.value||0)})}/></div>
        <div><label className="eyebrow">Quotation validity (days)</label><input type="number" value={f.quotation_validity_days} onChange={e => setF({...f, quotation_validity_days: parseInt(e.target.value||0)})}/></div>
      </div>
      <button className="btn-gold w-full" onClick={() => save(f)}>Save</button>
    </div>
  );
}

function Security({ logout, nav }) {
  const [me, setMe] = useState(null);
  const [pw, setPw] = useState({ current: '', new: '', confirm: '' });
  useEffect(() => { api.get('/auth/me').then(r => setMe(r.data)); }, []);
  const change = async () => {
    if (pw.new !== pw.confirm) return toast.error('Passwords do not match');
    if (pw.new.length < 10) return toast.error('Password must be ≥ 10 characters');
    try {
      const r = await api.post('/auth/change-password', { current: pw.current, new: pw.new });
      localStorage.setItem('rs_token', r.data.token);
      toast.success('Password changed. Other sessions revoked.');
      setPw({ current: '', new: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const logoutAll = async () => {
    if (!window.confirm('Sign out on all devices? You will be logged out here too.')) return;
    await api.post('/auth/logout-all');
    logout(); nav('/login');
  };
  return (
    <div className="space-y-4">
      <div className="card-cream p-5">
        <h3 className="serif text-[20px] mb-3">Account</h3>
        {me && <div className="text-xs text-[var(--muted)] mb-2">Last login: {me.last_login_at ? new Date(me.last_login_at).toLocaleString('en-GB') : '—'}{me.last_login_ip ? ` from ${me.last_login_ip}` : ''}</div>}
        <button className="btn-ghost text-sm" onClick={logoutAll} data-testid="logout-all">Log out of all devices</button>
      </div>
      <div className="card-cream p-5 space-y-3">
        <h3 className="serif text-[20px]">Change Password</h3>
        <div><label className="eyebrow">Current password</label><input type="password" value={pw.current} onChange={e => setPw({...pw, current: e.target.value})} data-testid="pw-current"/></div>
        <div><label className="eyebrow">New password (min 10)</label><input type="password" value={pw.new} onChange={e => setPw({...pw, new: e.target.value})} data-testid="pw-new"/></div>
        <div><label className="eyebrow">Confirm</label><input type="password" value={pw.confirm} onChange={e => setPw({...pw, confirm: e.target.value})} data-testid="pw-confirm"/></div>
        <button className="btn-gold w-full" onClick={change} data-testid="pw-save">Change Password</button>
      </div>
    </div>
  );
}

function Backup() {
  const download = async () => {
    const r = await api.get('/backup/download');
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rishika-backup-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Backup downloaded');
  };
  const restore = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!window.confirm(`Restore from ${file.name}? This overwrites current data.`)) return;
    const text = await file.text();
    const dump = JSON.parse(text);
    await api.post('/backup/restore', dump);
    toast.success('Restored. Reloading…');
    setTimeout(() => window.location.reload(), 1200);
  };
  return (
    <div className="card-cream p-5 space-y-4">
      <h3 className="serif text-[20px]">Backup & Restore</h3>
      <div className="text-xs text-[var(--muted)]">Downloads a single JSON containing every client, project, event, invoice, payment, quotation, assignment, expense and setting. Store it somewhere safe.</div>
      <button className="btn-gold w-full" onClick={download} data-testid="backup-download">Download full backup now</button>
      <div className="pt-3 border-t hairline">
        <label className="eyebrow block mb-2">Restore from backup</label>
        <input type="file" accept="application/json" onChange={restore} data-testid="backup-restore"/>
        <div className="text-[10px] text-[var(--muted)] mt-1">Uploading a backup replaces current data. Confirmation required.</div>
      </div>
    </div>
  );
}

function ICalTab({ s, save }) {
  const [token, setToken] = useState(s.ical_token || '');
  useEffect(() => setToken(s.ical_token || ''), [s]);
  const rotate = async () => {
    if (!window.confirm('Rotate iCal token? Previous URL stops working.')) return;
    const r = await api.post('/ical/rotate');
    setToken(r.data.token);
    toast.success('Token rotated');
  };
  const url = token ? `${process.env.REACT_APP_BACKEND_URL}/api/public/ical/${token}` : '';
  return (
    <div className="card-cream p-5 space-y-3">
      <h3 className="serif text-[20px]">Google Calendar Sync</h3>
      <div className="text-sm text-[var(--muted)]">Subscribe by URL — one-way, auto-updating feed of confirmed events. In Google Calendar: Other calendars → +  → From URL, paste the link below.</div>
      {!token && <button className="btn-gold" onClick={rotate}>Generate feed URL</button>}
      {token && (
        <>
          <div className="p-2 rounded font-mono text-xs break-all" style={{background:'var(--cream-alt)'}}>{url}</div>
          <div className="flex gap-2">
            <button className="btn-ghost text-xs" onClick={() => { navigator.clipboard.writeText(url); toast.success('Copied'); }} data-testid="ical-copy">Copy URL</button>
            <button className="btn-ghost text-xs" onClick={rotate} style={{color:'var(--overdue)'}}>Rotate (invalidate old)</button>
          </div>
        </>
      )}
    </div>
  );
}
